// Rummy lobby — LANDSCAPE layout.
//
// Visual parity with Ludo: the STRUCTURE of every top-bar element
// (back button pill, wallet chip, image-only settings) and the SHAPE
// of the tiles (gold border, 3-row stack, selected state behaviour)
// mirror Ludo exactly; the BASE COLOURS are Rummy's own green felt
// palette so each game keeps its identity.
//
// Top bar explicitly has no visible container — elements float above
// the felt directly (per user spec "no section divided for top bar").

import Icon from '@baaligames/shared-ui/components/Icon';
import { playSlotLock, playTap } from './sounds';

const CASH_IMAGES = [
  '/games/rummy/Cash-1.png',
  '/games/rummy/Cash-2.png',
  '/games/rummy/Cash-3.png',
  '/games/rummy/Cash-4.png',
];
// Card-themed placeholders for the 2P / 4P / 6P selector. Currently
// copied from Ludo's Pawn art; user will swap in card-stack graphics
// later. File names are pre-themed so the swap is drop-in.
const PLAYER_IMAGES: Record<2 | 4 | 6, string> = {
  2: '/games/rummy/Card-2.png',
  4: '/games/rummy/Card-4.png',
  6: '/games/rummy/Card-6.png',
};

type Props = {
  authReady: boolean;
  onlinePlayers: number;
  betTiers: number[];
  selectedBet: number | null;
  selectedPlayers: 2 | 4 | 6;
  platformFeePercent: number;
  waitingByBet: Record<string, number>;
  showWaitingBadge: boolean;
  soundOn: boolean;
  walletBalance: number;
  onSelectPlayers: (n: 2 | 4 | 6) => void;
  onSelectBet: (n: number) => void;
  onExit: () => void;
  onOpenSettings: () => void;
  onAddFunds: () => void;
};

function fmt(n: number) {
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

export default function LobbyScreen(p: Props) {
  // Rummy shows exactly 4 stakes to match Ludo and X&O. Take the top 4
  // non-zero tiers from config — admin can re-order them from the
  // admin panel.
  // Include zero-amount tiers too — they render as "FREE" so admins
  // can offer free-play stakes by setting a BET_TIERS entry to 0.
  const cashes = p.betTiers.slice(0, 4);
  return (
    <div className="rummy-lobby h-full w-full flex flex-col relative">
      {!p.authReady && (
        <div className="absolute inset-0 grid place-items-center z-50 bg-black/60">
          <div className="text-[#fde08b] font-bold tracking-widest">CONNECTING…</div>
        </div>
      )}

      {/* ─── Top bar: Back · Wallet · Settings ─────────────────────────
          Every className here is a literal copy of Ludo's top-bar shape
          (rounded-full pill + bg-black/30 + white/15 border for Back;
          rounded-full image-only for Settings). Wallet chip mirrors
          Ludo's gradient-pill with coin scale-150 + Plus button but
          swaps Ludo's purple for Rummy's green felt gradient. */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 z-10">
        <button
          type="button"
          onClick={() => { playTap(); p.onExit(); }}
          aria-label="Back"
          title="Back to home"
          className="w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center bg-black/30 border border-white/15 hover:bg-black/50 active:scale-95 transition text-white"
        >
          <Icon name="arrow_back" size={20} filled />
        </button>

        <div
          className="bg-gradient-to-b from-[#0a3b22] to-[#03190d] rounded-lg shrink-0 flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-[#a0a0a021] shadow-[0_0_30px_rgba(255,255,255,0.05)] relative"
          aria-label={`Balance ${p.walletBalance}`}
        >
          <img
            src="/games/rummy/Coin.png"
            alt=""
            className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10"
          />
          <span className="text-white font-bold text-base sm:text-lg mr-2 sm:mr-3 tracking-wide">
            {fmt(p.walletBalance)}
          </span>
          <button
            type="button"
            onClick={() => { playTap(); p.onAddFunds(); }}
            aria-label="Add funds"
            title="Add funds"
            className="rounded-sm flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
          >
            <img
              src="/games/rummy/Plus.png"
              alt=""
              className="w-6 h-6 sm:w-7 sm:h-7 object-contain"
            />
          </button>
        </div>

        <button
          type="button"
          onClick={() => { playTap(); p.onOpenSettings(); }}
          aria-label="Settings"
          title="Settings"
          className="w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center shadow-[0_0_5px_rgba(0,0,0,1)] hover:scale-105 active:scale-95 transition-transform"
        >
          <img
            src="/games/rummy/Settings.png"
            alt=""
            className="w-full h-full object-contain"
          />
        </button>
      </div>

      {/* ─── Body: 2-column grid ────────────────────────────────────── */}
      <div className="flex-1 grid grid-cols-[minmax(170px,1fr)_minmax(320px,1.6fr)] gap-3 px-3 pb-3 min-h-0">

        {/* LEFT — logo + online pill */}
        <div className="flex flex-col items-center justify-center gap-3 min-h-0">
          <img
            src="/games/rummy/Logo.png"
            alt=""
            className="object-contain drop-shadow-2xl w-full"
            style={{ maxHeight: 130, maxWidth: 200 }}
          />
          <div
            className="inline-flex items-center gap-2 bg-black/60 border border-[#fde08b]/50 text-[#fde08b] px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest whitespace-nowrap"
          >
            <span
              className="w-2 h-2 rounded-full bg-green-500"
              style={{ boxShadow: '0 0 6px rgba(34,197,94,0.8)' }}
            />
            <span>{p.onlinePlayers} PLAYERS ONLINE</span>
          </div>
        </div>

        {/* RIGHT — player-count selector + bet grid */}
        <div className="flex flex-col gap-3 min-h-0 justify-center">
          {/* Player-count row — Ludo pawn-btn shape (gold border + dark
              idle gradient) on Rummy's green-felt base, with the emerald/
              lime `rummy-pawn-btn.selected` state. */}
          <div>
            <div className="text-[10px] font-bold tracking-widest text-[#dfff7a] mb-1.5 uppercase">
              Players
            </div>
            <div className="flex gap-2">
              {[2, 4, 6].map((n) => {
                const isSelected = p.selectedPlayers === n;
                return (
                  <button
                    key={n}
                    data-players={n}
                    onClick={() => { playSlotLock(); p.onSelectPlayers(n as 2 | 4 | 6); }}
                    className={[
                      'rummy-pawn-btn flex-1 py-2 rounded-xl border-[3px] border-[#bc9648]',
                      'bg-gradient-to-b from-[#0a3b22] to-[#03190d]',
                      'shadow-[0_4px_10px_rgba(0,0,0,0.55)]',
                      'flex items-center justify-center gap-2',
                      isSelected ? 'selected' : '',
                    ].join(' ')}
                  >
                    <img
                      src={PLAYER_IMAGES[n as 2 | 4 | 6]}
                      alt=""
                      className="w-7 h-7 sm:w-9 sm:h-9 object-contain drop-shadow-xl pointer-events-none"
                    />
                    <div className="flex flex-col items-start leading-none">
                      <span
                        className="text-[#e8c66e] text-xl sm:text-2xl font-black leading-none"
                        style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                      >
                        {n}
                      </span>
                      <span
                        className={[
                          'text-[9px] font-bold tracking-widest',
                          isSelected ? 'text-[#dfff7a]' : 'text-[#bc9648]',
                        ].join(' ')}
                      >
                        PLAYER{n > 1 ? 'S' : ''}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bet grid — Ludo cash-btn SHAPE on Rummy's green-felt idle;
              emerald/lime selected state comes from `rummy-cash-btn
              .selected` in rummy.css. Exactly 4 tiles, 2×2 grid, to
              match the other games. */}
          <div>
            <div className="text-[10px] font-bold tracking-widest text-[#dfff7a] mb-1.5 uppercase">
              Select Stake
            </div>
            <div className="grid grid-cols-2 gap-2">
              {cashes.map((val, i) => {
                const winAmt = Math.floor(val * p.selectedPlayers * (100 - p.platformFeePercent) / 100);
                const waiting = Number(p.waitingByBet?.[String(val)] || 0);
                const showBadge = p.showWaitingBadge && waiting > 0;
                const isSelected = p.selectedBet === val;
                const cashImg = CASH_IMAGES[i % CASH_IMAGES.length];
                return (
                  <button
                    key={val}
                    data-val={val}
                    onClick={() => { playTap(); p.onSelectBet(val); }}
                    className={[
                      'rummy-cash-btn relative py-2 px-3 rounded-xl border-[3px] border-[#bc9648]',
                      'bg-gradient-to-b from-[#0a3b22] to-[#03190d]',
                      'shadow-[0_6px_15px_rgba(0,0,0,0.6)]',
                      'flex items-center justify-between gap-2',
                      isSelected ? 'selected' : '',
                    ].join(' ')}
                  >
                    {showBadge && (
                      <span
                        className="cash-waiting-badge"
                        aria-label={`${waiting} waiting`}
                        title={`${waiting} waiting at this stake`}
                      >
                        <Icon name="group" size={10} className="cash-waiting-badge__icon" />
                        <span className="cash-waiting-badge__num">{waiting}</span>
                      </span>
                    )}
                    <div className="flex flex-col items-start min-w-0">
                      <span
                        className={
                          'text-white font-black leading-none tabular-nums '
                          + (val === 0 ? 'text-lg sm:text-xl' : 'text-2xl sm:text-3xl')
                        }
                        style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                      >
                        {val === 0 ? 'FREE' : fmt(val)}
                      </span>
                      <span
                        className="cash-win-label text-[#dfff7a] font-bold text-[9px] sm:text-[10px] tracking-widest mt-0.5"
                        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}
                      >
                        {val === 0 ? 'PLAY FOR FUN' : `WIN ${fmt(winAmt)}`}
                      </span>
                    </div>
                    <img
                      src={cashImg}
                      alt=""
                      className="w-10 h-10 sm:w-12 sm:h-12 object-contain drop-shadow-lg pointer-events-none"
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
